import React, { useState } from 'react'
import classnames from 'classnames'
import UploadImages from 'yagoubi-upload-images'
import { useTranslation } from 'react-i18next'

import FormDescription from '../../../components/shared/wysiwyg/form-description'
import { getBase64 } from '../../../components/shared/file/get-file-data'
import {
    saveDeviceServiceHistory,
    saveDeviceServiceHistoryImages,
} from '../service-history-helpers'
import { uploadServiceImage } from '../../../components/shared/file-uploader'
import { generateUUID } from '../../../components/shared/uuid-generator'
import Loader from '../../../components/shared/loader'
import { useAppSelector } from '../../../hooks/redux'

import type { DeviceEntity } from '../detail/parse-device-data'

interface ServiceHistoryFormState {
    name: string
    description: string
    showForAll: boolean
    images: string[]
    reportType: string
}

interface ServiceHistoryFormProps {
    device?: DeviceEntity
    onSuccess?: () => void
    hideShowAll?: boolean
    reportType?: string
}

interface AuthedUser {
    superAdmin?: boolean
}

/**
 * Form for creating a new device service history entry.
 *
 * Persists the entry first, then uploads each attached image and
 * patches the entry with the resulting URLs.
 */
const ServiceHistoryForm = ({
    device,
    onSuccess,
    hideShowAll,
    reportType = 'normal',
}: ServiceHistoryFormProps) => {
    const { t } = useTranslation()
    const authedUser = useAppSelector(state => state.auth.user) as AuthedUser | null

    const [form, setForm] = useState<ServiceHistoryFormState>({
        name: '',
        description: '',
        showForAll: true,
        images: [],
        reportType,
    })
    const [busyForm, setBusyForm] = useState(false)

    const setFormData = <K extends keyof ServiceHistoryFormState>(
        key: K,
        value: ServiceHistoryFormState[K],
    ): void => {
        setForm(previous => ({
            ...previous,
            [key]: value,
        }))
    }

    const setImages = (images: File[]): void => {
        Promise.all(images.map(image => getBase64(image)))
            .then(base64Images => setFormData('images', base64Images as string[]))
    }

    const saveDeviceHistoryForm = (): Promise<void> => {
        if (!device) {
            return Promise.resolve()
        }

        setBusyForm(true)

        return saveDeviceServiceHistory(device as { uid: string; [key: string]: unknown }, { ...form, images: [] })
            .then((deviceServiceHistory) => (
                Promise.all(form.images.map(base64Image =>
                    uploadServiceImage(device.uid as string, generateUUID(), base64Image)))
                    .then(savedImages => saveDeviceServiceHistoryImages(deviceServiceHistory ?? null, savedImages))
                    .then(() => setBusyForm(false))
            ))
    }

    return (
        <div>
            <div className="form-group">
                <label htmlFor="device-form-name">
                    {t('deviceServiceHistory.form.name')}
                </label>
                <input
                    type="text"
                    className="form-control"
                    id="device-form-name"
                    placeholder={t('deviceServiceHistory.form.name')}
                    value={form.name || ''}
                    onChange={event => setFormData('name', event.target.value)}
                />
            </div>

            <div className="form-group">
                <label htmlFor="device-form-description">
                    {t('deviceServiceHistory.form.description')}
                </label>
                <FormDescription
                    description=""
                    changeDescription={(content: string) => setFormData('description', content)}
                    maxLength={10000}
                    classNames="p-2"
                />
            </div>

            {(!hideShowAll && authedUser?.superAdmin) && (
                <div className="form-group">
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="device-form-show-all"
                            onChange={event => setFormData('showForAll', event.target.checked)}
                            checked={form.showForAll || false}
                        />
                        <label className="form-check-label" htmlFor="device-form-show-all">
                            {t('deviceServiceHistory.form.showAll')}
                        </label>
                    </div>
                </div>
            )}

            <div className="form-group">
                <label htmlFor="device-form-images">
                    {t('deviceServiceHistory.form.uploadPhotos')}
                </label>
                <UploadImages
                    multiple
                    onChange={setImages}
                    placeholder={t('deviceServiceHistory.form.uploadPhotos')}
                />
            </div>

            <div className="form-group">
                <div
                    className={classnames({
                        'btn btn-block': true,
                        'btn-success': !busyForm,
                        'btn-outline-success disabled': busyForm,
                    })}
                    onClick={busyForm ? () => {} : () => {
                        saveDeviceHistoryForm().then(() => onSuccess?.())
                    }}
                >
                    {busyForm && <Loader width={17} />}
                    {!busyForm && t('deviceServiceHistory.form.save')}
                </div>
            </div>
        </div>
    )
}

export default ServiceHistoryForm
